import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const CookiePolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Button onClick={() => navigate('/')} variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a la tienda
          </Button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Política de Cookies</h1>
            <p className="text-sm text-gray-500 mb-8">Última actualización: Febrero 2025</p>

            <div className="prose prose-gray max-w-none space-y-6">
              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">1. ¿Qué son las Cookies?</h2>
                <p className="text-gray-600">
                  Las cookies son pequeños archivos de texto que los sitios web almacenan en su dispositivo (ordenador, tablet, móvil) cuando los visita. Permiten que el sitio recuerde sus acciones y preferencias durante un período de tiempo, para que no tenga que volver a introducirlos cada vez que visite el sitio o navegue de una página a otra.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">2. ¿Qué Cookies Utilizamos?</h2>
                
                <h3 className="text-lg font-semibold text-gray-700 mt-4 mb-2">Cookies Técnicas (Necesarias)</h3>
                <p className="text-gray-600">Son esenciales para el funcionamiento del sitio web. Sin ellas, no podría funcionar correctamente.</p>
                <table className="w-full mt-3 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 text-left">Cookie</th>
                      <th className="p-2 text-left">Finalidad</th>
                      <th className="p-2 text-left">Duración</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b">
                      <td className="p-2">jrf-cart</td>
                      <td className="p-2">Almacena los productos del carrito</td>
                      <td className="p-2">Sesión</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2">jrf-coupon</td>
                      <td className="p-2">Almacena el cupón aplicado</td>
                      <td className="p-2">Sesión</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2">jrf-carrier</td>
                      <td className="p-2">Transportista seleccionado</td>
                      <td className="p-2">Sesión</td>
                    </tr>
                  </tbody>
                </table>

                <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-2">Cookies de Terceros</h3>
                <p className="text-gray-600">Utilizadas por servicios externos integrados en nuestra web.</p>
                <table className="w-full mt-3 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 text-left">Proveedor</th>
                      <th className="p-2 text-left">Finalidad</th>
                      <th className="p-2 text-left">Más info</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b">
                      <td className="p-2">Stripe</td>
                      <td className="p-2">Procesamiento seguro de pagos</td>
                      <td className="p-2"><a href="https://stripe.com/es/privacy" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">Política</a></td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2">PayPal</td>
                      <td className="p-2">Procesamiento seguro de pagos</td>
                      <td className="p-2"><a href="https://www.paypal.com/es/webapps/mpp/ua/privacy-full" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">Política</a></td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">3. ¿Cómo Gestionar las Cookies?</h2>
                <p className="text-gray-600">
                  Puede configurar su navegador para rechazar todas las cookies o para que le avise cuando se envía una cookie. Sin embargo, si bloquea todas las cookies, es posible que algunas funciones del sitio no funcionen correctamente.
                </p>
                <p className="text-gray-600 mt-3">
                  <strong>Cómo desactivar cookies en los principales navegadores:</strong>
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-2">
                  <li>
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">Google Chrome</a>
                  </li>
                  <li>
                    <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">Mozilla Firefox</a>
                  </li>
                  <li>
                    <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">Safari</a>
                  </li>
                  <li>
                    <a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">Microsoft Edge</a>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">4. Base Legal</h2>
                <p className="text-gray-600">
                  El uso de cookies se rige por:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-2">
                  <li>Ley 34/2002, de 11 de julio, de servicios de la sociedad de la información y de comercio electrónico (LSSI-CE)</li>
                  <li>Reglamento (UE) 2016/679 (RGPD)</li>
                  <li>Directiva 2002/58/CE sobre privacidad y comunicaciones electrónicas</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">5. Actualizaciones</h2>
                <p className="text-gray-600">
                  Podemos actualizar esta Política de Cookies periódicamente. Le recomendamos revisarla con frecuencia para estar informado sobre cómo utilizamos las cookies.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">6. Contacto</h2>
                <p className="text-gray-600">
                  Si tiene preguntas sobre nuestra política de cookies, puede contactarnos en:
                </p>
                <p className="text-gray-600 mt-2">
                  <strong>Email:</strong> infojrfsportswear@gmail.com
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
